import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { SuperAdminSidebar } from "@/components/dashboard/superadmin-sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ThemeToggle } from "@/components/theme-toggle";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, User, Shield, FileText, Settings, Download, Upload, LogIn, Database, UserPlus, Trash2, Edit } from "lucide-react";

interface ActivityLog {
  id: string;
  action: string;
  resource: string;
  resourceId?: string;
  userId: string;
  userName?: string;
  userRole?: string;
  details?: any;
  ipAddress?: string;
  createdAt: string;
}

const getActionIcon = (action: string) => {
  if (action.includes("login")) return LogIn;
  if (action.includes("download")) return Download;
  if (action.includes("upload")) return Upload;
  if (action.includes("create")) return UserPlus;
  if (action.includes("delete")) return Trash2;
  if (action.includes("update") || action.includes("edit")) return Edit;
  if (action.includes("nav")) return Database;
  if (action.includes("admin")) return Shield;
  if (action.includes("statement")) return FileText;
  if (action.includes("setting")) return Settings;
  return Activity;
};

const getActionColor = (action: string) => {
  if (action.includes("create") || action.includes("add") || action.includes("login")) return "default";
  if (action.includes("delete") || action.includes("remove")) return "destructive";
  if (action.includes("update") || action.includes("edit") || action.includes("download")) return "secondary";
  return "outline";
};

const getRoleBadgeColor = (role: string) => {
  if (role === "Super Admin") return "destructive";
  if (role === "Admin") return "default";
  if (role === "Investor") return "secondary";
  return "outline";
};

export default function SuperAdminActivityPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAction, setFilterAction] = useState("all");
  const [filterRole, setFilterRole] = useState("all");

  const { data: activityLogs, isLoading } = useQuery<ActivityLog[]>({
    queryKey: ["/api/superadmin/activity-logs"],
  });

  // Filter and search logic
  const filteredLogs = activityLogs?.filter((log) => {
    const matchesSearch = 
      log.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesActionFilter = filterAction === "all" || log.action.includes(filterAction);
    const matchesRoleFilter = filterRole === "all" || log.userRole === filterRole;

    return matchesSearch && matchesActionFilter && matchesRoleFilter;
  }) || [];

  const sidebarStyle = {
    "--sidebar-width": "17rem",
    "--sidebar-width-icon": "3.5rem",
  };

  return (
    <SidebarProvider style={sidebarStyle as React.CSSProperties}>
      <div className="flex h-screen w-full overflow-hidden">
        <SuperAdminSidebar />
        <div className="flex flex-col flex-1 h-full overflow-hidden">
          <header className="flex items-center justify-between gap-4 h-16 px-6 border-b bg-background/95 backdrop-blur shrink-0">
            <div className="flex items-center gap-4">
              <SidebarTrigger data-testid="superadmin-button-sidebar-toggle" />
              <div>
                <h1 className="text-lg font-semibold">Activity Logs</h1>
                <p className="text-sm text-muted-foreground">
                  {filteredLogs.length} {filteredLogs.length === 1 ? 'activity' : 'activities'} recorded
                </p>
              </div>
            </div>
            <ThemeToggle />
          </header>
          
          <main className="flex-1 p-6 space-y-6 overflow-y-auto">
            {/* Filters Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-semibold">Filters</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Search</label>
                    <Input
                      placeholder="Search by name or action..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Action Type</label>
                    <Select value={filterAction} onValueChange={setFilterAction}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Actions" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Actions</SelectItem>
                        <SelectItem value="login">Login</SelectItem>
                        <SelectItem value="download">Downloads</SelectItem>
                        <SelectItem value="upload">Uploads</SelectItem>
                        <SelectItem value="create">Create</SelectItem>
                        <SelectItem value="update">Update</SelectItem>
                        <SelectItem value="delete">Delete</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">User Role</label>
                    <Select value={filterRole} onValueChange={setFilterRole}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Roles" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="Super Admin">Super Admin</SelectItem>
                        <SelectItem value="Admin">Admin</SelectItem>
                        <SelectItem value="Investor">Investor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Activity Logs Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : filteredLogs.length > 0 ? (
                  <div className="space-y-0 border rounded-lg">
                    {filteredLogs.map((log, idx) => {
                      const IconComponent = getActionIcon(log.action);
                      return (
                        <div
                          key={log.id}
                          className={`flex items-start gap-4 p-4 hover:bg-muted/50 transition-colors ${idx !== filteredLogs.length - 1 ? 'border-b' : ''}`}
                          data-testid={`activity-log-${log.id}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0 space-y-2">
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-semibold text-base">{log.userName || "Unknown User"}</p>
                                <Badge variant={getRoleBadgeColor(log.userRole || "Unknown")} className="text-xs">
                                  {log.userRole || "Unknown"}
                                </Badge>
                              </div>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {new Date(log.createdAt).toLocaleString()}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant={getActionColor(log.action)} className="text-xs font-mono">
                                {log.action}
                              </Badge>
                              <span className="text-sm text-muted-foreground">on</span>
                              <Badge variant="outline" className="text-xs">
                                {log.resource}
                              </Badge>
                              {log.resourceId && (
                                <span className="text-xs text-muted-foreground font-mono">
                                  #{log.resourceId.slice(0, 8)}
                                </span>
                              )}
                            </div>
                            
                            {log.ipAddress && (
                              <p className="text-xs text-muted-foreground">
                                IP: {log.ipAddress}
                              </p>
                            )}

                            {log.details && Object.keys(log.details).length > 0 && (
                              <details className="text-xs">
                                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">View Details</summary>
                                <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                                  {JSON.stringify(log.details, null, 2)}
                                </pre>
                              </details>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-sm text-muted-foreground">
                      {searchTerm || filterAction !== "all" || filterRole !== "all"
                        ? "No activities match your filters"
                        : "No activity logs found"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
